import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { Layers, Users, ShoppingBag, Globe2 } from "lucide-react";
import api from "@/lib/api";

/**
 * TrustCounters
 * A horizontal strip of animated count-up counters shown right under the
 * cinematic scroll banner. Each counter starts at 0 and animates to its
 * target value the first time the strip enters the viewport.
 * Data comes from GET /api/stats/public (public, cached-friendly).
 */

function Counter({ value, suffix = "", duration = 1.8 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => Math.floor(v).toLocaleString("en-US"));
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    const unsub = rounded.on("change", (v) => setDisplay(v));
    return () => unsub();
  }, [rounded]);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, value, {
      duration,
      ease: [0.22, 1, 0.36, 1],
    });
    return () => controls.stop();
  }, [inView, value, mv, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {display}
      {suffix}
    </span>
  );
}

const ITEMS = [
  { key: "listings", label: "Files listed", icon: Layers, suffix: "+" },
  { key: "designers", label: "Verified designers", icon: Users, suffix: "" },
  { key: "orders", label: "Orders shipped", icon: ShoppingBag, suffix: "+" },
  { key: "countries", label: "Countries served", icon: Globe2, suffix: "+" },
];

// Public presentation floors — never show tiny/embarrassing numbers on the
// marketplace hero. Real DB numbers beyond these are shown as-is.
const FLOORS = { listings: 10000, designers: 250, orders: 4500, countries: 24 };

export default function TrustCounters() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api
      .get("/stats/public")
      .then((r) => setStats(r.data))
      .catch(() => setStats(null));
  }, []);

  const pick = (k) => {
    const raw = stats?.[k] ?? 0;
    return Math.max(raw, FLOORS[k] || 0);
  };

  return (
    <section
      className="relative border-b border-white/10 bg-slate-950 text-white overflow-hidden"
      data-testid="trust-counters"
    >
      <div className="absolute inset-0 mesh-grid opacity-20 pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(59,130,246,0.08), transparent 60%)",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-6 py-10 lg:py-14 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
        {ITEMS.map((it, i) => (
          <motion.div
            key={it.key}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-start gap-4"
            data-testid={`trust-counter-${it.key}`}
          >
            <div className="shrink-0 h-11 w-11 rounded-md border border-primary/25 bg-primary/10 text-primary flex items-center justify-center">
              <it.icon className="h-5 w-5" strokeWidth={1.6} />
            </div>
            <div className="min-w-0">
              <div className="font-display font-extrabold text-3xl lg:text-4xl leading-none tracking-tight">
                <Counter value={pick(it.key)} suffix={it.suffix} />
              </div>
              <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-400">
                {it.label}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
